// src/components/Pages/ProfileSettings.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Camera, Save, X } from "lucide-react";
import Button from "../UI/Button";

const CONTROL_H = "h-11";

const CONTROL = `${CONTROL_H} w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`;

const LABEL = "block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1";
const HELP = "text-xs text-gray-500 dark:text-gray-400 mt-1";

const CARD =
  "rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm";

const CARD_HOVER = "hover:shadow-md transition-shadow";

const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5MB

const Card = ({ title, description, children, right }) => (
  <div className={`${CARD} ${CARD_HOVER}`}>
    <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h3>
        {description ? (
          <p className="mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            {description}
          </p>
        ) : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>

    <div className="p-6">{children}</div>
  </div>
);

const toAbsoluteAvatarUrl = (url) => {
  const s = String(url || "").trim();

  if (!s || s === "null" || s === "undefined") return "";
  if (s.startsWith("blob:") || s.startsWith("data:")) return s;
  if (/^https?:\/\//i.test(s)) return s;

  const base = String(
    process.env.REACT_APP_API_BASE ||
      process.env.REACT_APP_API_URL ||
      "http://localhost:5000"
  ).replace(/\/+$/, "");

  if (s.startsWith("/")) return `${base}${s}`;
  if (s.startsWith("uploads/")) return `${base}/${s}`;

  return `${base}/${s.replace(/^\/+/, "")}`;
};

const getInitials = (firstName = "", lastName = "") => {
  const a = String(firstName || "").trim().charAt(0);
  const b = String(lastName || "").trim().charAt(0);
  return `${a}${b}`.toUpperCase() || "?";
};

const ProfileSettings = ({
  initialUser = {
    id: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "Staff",
    avatarUrl: "",
  },
  onSave,
  onCancel,
  roleReadOnly = true,
}) => {
  const [form, setForm] = useState(() => ({
    ...initialUser,
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    avatarFile: null,
  }));

  const [loading, setLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(() =>
    toAbsoluteAvatarUrl(initialUser.avatarUrl || "")
  );
  const [avatarOk, setAvatarOk] = useState(true);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const safeAvatar = toAbsoluteAvatarUrl(initialUser.avatarUrl || "");

    setForm({
      ...initialUser,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      avatarFile: null,
    });
    setAvatarPreview(safeAvatar);
    setAvatarOk(!!safeAvatar || false);

    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [initialUser]);

  const initials = useMemo(
    () => getInitials(form.firstName, form.lastName),
    [form.firstName, form.lastName]
  );

  const setVal = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = /^image\//i.test(file.type);
    if (!isImage) {
      toast.error("Please select a valid image file.");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_AVATAR_BYTES) {
      toast.error("Image must be 5MB or smaller.");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";

      setAvatarPreview(result);
      setAvatarOk(!!result);

      setForm((prev) => ({
        ...prev,
        avatarFile: file,
      }));

      toast.success("Profile photo selected");
    };

    reader.onerror = () => {
      toast.error("Failed to load image preview.");
    };

    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const handleSave = async () => {
    const toastId = "profile-save";

    try {
      if (!String(form.firstName || "").trim()) {
        toast.error("First name is required.");
        return;
      }

      if (!String(form.lastName || "").trim()) {
        toast.error("Last name is required.");
        return;
      }

      if (!String(form.email || "").trim()) {
        toast.error("Email address is required.");
        return;
      }

      if (form.newPassword || form.confirmPassword || form.currentPassword) {
        if (!form.currentPassword) {
          toast.error("Current password is required to change password.");
          return;
        }

        if (!form.newPassword) {
          toast.error("New password is required.");
          return;
        }

        if (form.newPassword.length < 8) {
          toast.error("New password must be at least 8 characters.");
          return;
        }

        if (form.newPassword !== form.confirmPassword) {
          toast.error("New password and confirm password do not match.");
          return;
        }
      }

      setLoading(true);
      toast.loading("Saving profile...", { id: toastId });

      const payload = { ...form };

      if (roleReadOnly) delete payload.role;
      delete payload.avatarUrl;

      await onSave?.(payload);

      toast.success("Profile updated successfully", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to save changes.", { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const safeAvatar = toAbsoluteAvatarUrl(initialUser.avatarUrl || "");

    setForm({
      ...initialUser,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      avatarFile: null,
    });
    setAvatarPreview(safeAvatar);
    setAvatarOk(!!safeAvatar || false);

    if (fileInputRef.current) fileInputRef.current.value = "";

    onCancel?.();
    toast("Changes discarded");
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 dark:bg-gray-950 transition-colors">
      <div className="w-full min-w-0 p-4 sm:p-6 space-y-6 pb-28 lg:pb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Profile Settings
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Update your personal information and password
            </p>
          </div>

          <div className="hidden lg:flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              className={`px-5 ${CONTROL_H} rounded-xl`}
            >
              <span className="inline-flex items-center gap-2">
                <X className="h-4 w-4" />
                Cancel
              </span>
            </Button>

            <Button
              onClick={handleSave}
              loading={loading}
              className={`px-5 ${CONTROL_H} rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white`}
            >
              <span className="inline-flex items-center gap-2">
                <Save className="h-4 w-4" />
                Save Changes
              </span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-1 space-y-6">
            <Card
              title="Profile Photo"
              description="This will be shown on your account."
            >
              <div className="flex items-center gap-4">
                {avatarPreview && avatarOk ? (
                  <img
                    key={avatarPreview}
                    src={avatarPreview}
                    alt="Avatar"
                    className="w-16 h-16 rounded-full object-cover border border-gray-200 dark:border-gray-700 shrink-0"
                    onError={() => {
                      setAvatarOk(false);
                      setAvatarPreview("");
                    }}
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/30 dark:to-blue-950/20 flex items-center justify-center text-indigo-600 dark:text-indigo-200 text-xl font-semibold border border-indigo-100 dark:border-indigo-900/40">
                    {initials}
                  </div>
                )}

                <div className="flex flex-col gap-2 w-full">
                  <label
                    htmlFor="profile-avatar-upload"
                    className={`inline-flex items-center justify-center gap-2 px-4 ${CONTROL_H} rounded-xl border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-full`}
                  >
                    <Camera className="h-4 w-4" />
                    <span>Upload Photo</span>
                  </label>

                  <input
                    id="profile-avatar-upload"
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />

                  <p className={HELP}>JPG, PNG, or WEBP. Max 5MB.</p>
                </div>
              </div>
            </Card>

            <Card title="Account" description="Your account details.">
              <div>
                <label className={LABEL}>Role</label>
                <input
                  value={form.role || ""}
                  disabled={roleReadOnly}
                  onChange={(e) => setVal("role", e.target.value)}
                  className={`${CONTROL} ${
                    roleReadOnly ? "opacity-80 cursor-not-allowed" : ""
                  }`}
                />
                {roleReadOnly ? (
                  <p className={HELP}>Role is managed by the admin.</p>
                ) : null}
              </div>
            </Card>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <Card
              title="Personal Information"
              description="Make sure your details are correct."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={form.firstName || ""}
                    onChange={(e) => setVal("firstName", e.target.value)}
                    placeholder="Enter first name"
                    className={CONTROL}
                  />
                </div>

                <div>
                  <label className={LABEL}>
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={form.lastName || ""}
                    onChange={(e) => setVal("lastName", e.target.value)}
                    placeholder="Enter last name"
                    className={CONTROL}
                  />
                </div>

                <div>
                  <label className={LABEL}>Middle Name</label>
                  <input
                    value={form.middleName || ""}
                    onChange={(e) => setVal("middleName", e.target.value)}
                    placeholder="Enter middle name"
                    className={CONTROL}
                  />
                </div>

                <div>
                  <label className={LABEL}>
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => setVal("email", e.target.value)}
                    placeholder="Enter email address"
                    className={CONTROL}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className={LABEL}>Phone Number</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.phone || ""}
                    onChange={(e) => {
                      const onlyDigits = e.target.value.replace(/\D/g, "");
                      setVal("phone", onlyDigits);
                    }}
                    placeholder="Enter phone number"
                    className={CONTROL}
                  />
                </div>
              </div>
            </Card>

            <Card
              title="Change Password"
              description="Leave blank if you don’t want to change it."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={LABEL}>Current Password</label>
                  <input
                    type="password"
                    value={form.currentPassword || ""}
                    onChange={(e) => setVal("currentPassword", e.target.value)}
                    placeholder="Enter current password"
                    className={CONTROL}
                  />
                </div>

                <div>
                  <label className={LABEL}>New Password</label>
                  <input
                    type="password"
                    value={form.newPassword || ""}
                    onChange={(e) => setVal("newPassword", e.target.value)}
                    placeholder="Enter new password"
                    className={CONTROL}
                  />
                </div>

                <div>
                  <label className={LABEL}>Confirm New Password</label>
                  <input
                    type="password"
                    value={form.confirmPassword || ""}
                    onChange={(e) => setVal("confirmPassword", e.target.value)}
                    placeholder="Confirm new password"
                    className={CONTROL}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div
          className="lg:hidden fixed left-0 right-0 bottom-0 bg-white/95 dark:bg-gray-900/95 border-t border-gray-200 dark:border-gray-800 p-3 backdrop-blur z-50"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}
        >
          <div className="w-full min-w-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-1">
            <Button
              variant="outline"
              onClick={handleCancel}
              className={`w-full sm:w-auto px-5 ${CONTROL_H} rounded-xl`}
            >
              Cancel
            </Button>

            <Button
              onClick={handleSave}
              loading={loading}
              className={`w-full sm:w-auto px-5 ${CONTROL_H} rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white`}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;